/**
 * MobX store for the admin "Re-migrate Global-E platform store" card: portal selection,
 * last completed migration attempt, editable Global-E merchant id and environment, and remigration POST.
 */
import { makeAutoObservable, runInAction } from "mobx";
import AdminMigrateGlobalEMerchantsService, {
    GlobalEEnvironment,
    ILastCompletedShopMigrationAttempt,
} from "../../../../services/api/admin-migrate-global-e-merchants-service";
import { rootContainer, TYPE } from "../../../../services/container/root";
import toastMessageListStore from "../../../../components/toast-messages-modal/toast-messages-store";
import { IShop } from "../../../../models/User";
import { isSet } from "../../../../models/model-types-methods";

function parseGlobalEEnvironment(value: string | null | undefined): GlobalEEnvironment | undefined {
    if (!isSet(value)) {
        return undefined;
    }
    const allowed = Object.values(GlobalEEnvironment) as string[];
    return allowed.includes(value) ? (value as GlobalEEnvironment) : undefined;
}

class ReMigrateGlobalePlatformStore {
    private _selectedShop: IShop | null | undefined = null;
    private _merchantId = "";
    private _environment: GlobalEEnvironment | undefined = undefined;
    private _lastCompletedMigrationAttempt: ILastCompletedShopMigrationAttempt | null = null;
    private _isProcessing = false;
    private _errorMessage: string | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get service(): AdminMigrateGlobalEMerchantsService {
        return rootContainer.get<AdminMigrateGlobalEMerchantsService>(TYPE.AdminMigrateGlobalEMerchantsService);
    }

    public get portal(): IShop | null | undefined {
        return this._selectedShop;
    }

    public get merchantId(): string {
        return this._merchantId;
    }

    public get environment(): GlobalEEnvironment | undefined {
        return this._environment;
    }

    public get lastCompletedMigrationAttempt(): ILastCompletedShopMigrationAttempt | null {
        return this._lastCompletedMigrationAttempt;
    }

    public get isProcessing(): boolean {
        return this._isProcessing;
    }

    public get errorMessage(): string | null {
        return this._errorMessage;
    }

    public get isValid(): boolean {
        const portalId = this._selectedShop?.id;
        return (
            isSet(portalId) &&
            this._merchantId.trim().length > 0 &&
            isSet(this._environment)
        );
    }

    public setPortal(shop: IShop | null | undefined): void {
        this._selectedShop = shop;
        this._errorMessage = null;
        if (!isSet(shop)) {
            this._merchantId = "";
            this._environment = undefined;
            this._lastCompletedMigrationAttempt = null;
            return;
        }
        this._merchantId = (shop.global_e_user_id ?? "").trim();
        this._environment = parseGlobalEEnvironment(shop.global_e_env);
        void this.refreshLastCompletedAttempt();
    }

    public setMerchantId(value: string): void {
        this._merchantId = value;
    }

    public setEnvironment(env: GlobalEEnvironment): void {
        this._environment = env;
    }

    private reset(): void {
        this._selectedShop = null;
        this._merchantId = "";
        this._environment = undefined;
        this._lastCompletedMigrationAttempt = null;
        this._errorMessage = null;
    }

    private async refreshLastCompletedAttempt(): Promise<void> {
        const shop = this._selectedShop;
        if (!isSet(shop)) {
            return;
        }
        runInAction(() => {
            this._lastCompletedMigrationAttempt = null;
        });
        try {
            const attempt = await this.service.fetchLastCompletedShopMigrationAttempt(shop.id);
            runInAction(() => {
                this._lastCompletedMigrationAttempt = attempt;
            });
        } catch (e) {
            console.error(e);
            runInAction(() => {
                this._lastCompletedMigrationAttempt = null;
            });
        }
    }

    public async runMigration(): Promise<void> {
        if (!this.isValid || this._isProcessing) {
            return;
        }
        const portalId = this._selectedShop!.id;
        const merchantId = this._merchantId.trim();
        const environment = this._environment as GlobalEEnvironment;

        this._isProcessing = true;
        this._errorMessage = null;
        try {
            await this.service.remigrateGlobalEMerchant({
                portal_id: portalId,
                merchantId,
                environment,
            });
            runInAction(() => {
                toastMessageListStore.add({
                    title: "Success",
                    description: `Global-E platform re-migration finished for merchant "${merchantId}"`,
                });
                this.reset();
            });
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : "Unknown error during re-migration.";
            runInAction(() => {
                this._errorMessage = message;
            });
        } finally {
            runInAction(() => {
                this._isProcessing = false;
            });
        }
    }
}

export default new ReMigrateGlobalePlatformStore();
